import { ChevronDown16Regular, ChevronRight16Regular, Diamond12Regular, PanelRight16Regular, QuestionCircle12Regular, TableSimple16Regular } from "@fluentui/react-icons";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { DateCalendar } from "@/components/date-field";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isISODate } from "@/engine/date";
import type { HealthIssue } from "@/engine/health";
import { formatRemaining, remainingInfo, type RemainingInfo, type RemainingKind } from "@/engine/remaining";
import type { Task } from "@/engine/types";
import { REMAINING_TONE } from "@/features/gantt/RemainingLabel";
import { HEADER_H, type RowsViewport } from "@/features/workspace/Workspace";
import { nextGridWidthForHiddenColumns } from "@/features/workspace/grid-splitter";
import {
  bestFitGridWidth,
  bestFitNameColumnWidth,
  issueTaskIdSet,
  measureNameText,
  nameColumnFontFromElement,
  nameFitRowsFromProject,
  visibleNameColumnWidth,
} from "@/features/workspace/name-column-fit";
import { columnWritable } from "@/features/access/plan-surface";
import { isOwnScheduleTask } from "@/features/access/own-assignment";
import { usePlanSurface, useSelfRef } from "@/features/access/use-plan-access";
import { hintBlockedEdit } from "@/features/access/read-only-hint";
import { formatDate } from "@/lib/date-format";
import { modKey } from "@/lib/format";
import { cn } from "@/lib/utils";
import { DEFAULT_HIDDEN_COLUMNS, useProjectStore, type PinBoundary } from "@/store/project-store";
import { countLinkedExecution, tasksLinkedTo } from "@/features/tasks/task-query";
import { formatPredecessors } from "@/store/tree-ops";

export type ColKey = "row" | "wbs" | "name" | "assignee" | "mm" | "duration" | "start" | "finish" | "float" | "percent" | "preds" | "tasks" | "gate" | "remaining" | "note";

interface Col {
  key: ColKey;
  label: string;
  width: number;
  align?: "left" | "right" | "center";
  editable?: boolean;
  /** short help shown in the header tooltip */
  help?: string;
}

const GATE_GLYPH_TITLE: Record<string, string> = { "✓": "모든 검수 통과", "✕": "반려된 검수가 있습니다", "⏳": "검수를 기다리는 항목이 있습니다", "○": "아직 확인 전" };
function gateGlyph(key: string): string {
  const [ratio = "", failed = "0", ready = "0"] = key.split("|");
  if (Number(failed) > 0) return "✕";
  if (ratio.split("/")[0] === ratio.split("/")[1]) return "✓";
  return Number(ready) > 0 ? "⏳" : "○";
}

export const COLS: Col[] = [
  { key: "row", label: "", width: 32, align: "right" },
  { key: "wbs", label: "WBS", width: 56, help: "작업의 위치 번호입니다. 자동으로 매겨지며, 들여쓰기(⇥)·내어쓰기(⇧⇥)·위아래 이동(⌥↑↓)을 하면 다시 매겨집니다." },
  { key: "name", label: "작업", width: 240, editable: true, help: "작업 이름입니다. 하위 작업을 넣으면 '단계'가 되어 기간과 진척이 하위에서 자동으로 합쳐집니다. ↩ 편집 · ⌘↩ 아래에 추가 · ⇧⌘↩ 하위 추가" },
  { key: "assignee", label: "담당", width: 84, editable: true, help: "작업을 수행하는 담당 인력입니다. 인력풀과 연동됩니다." },
  { key: "mm", label: "M/M", width: 60, align: "right", editable: true, help: "작업에 투입되는 맨먼스(Man-Month) 공수입니다. 기본값은 근무일수 / 21일로 자동 산출됩니다." },
  { key: "duration", label: "기간", width: 56, align: "right", editable: true, help: "일하는 날만 센 일수입니다. 주말과 공휴일은 빠지므로 5d는 달력으로 일주일쯤입니다. 0d로 두면 마일스톤(하루짜리 이벤트)이 됩니다." },
  { key: "start", label: "시작", width: 92, align: "right", editable: true, help: "앞 작업이 끝나는 날에 맞춰 자동으로 정해집니다. 날짜를 직접 넣으면 '이 날 이후에 시작'으로 고정되어 자동 계산보다 우선합니다. 종료일보다 뒤는 고를 수 없고, 같은 날은 허용됩니다. 고정은 인스펙터에서 풀 수 있습니다." },
  { key: "finish", label: "종료", width: 92, align: "right", editable: true, help: "시작일에 기간을 더한 날입니다. 날짜를 바꾸면 기간이 맞춰집니다. 시작일보다 앞은 고를 수 없고, 같은 날은 허용됩니다." },
  { key: "float", label: "여유", width: 56, align: "right", help: "플랜 종료일을 늦추지 않고 이 작업이 미뤄질 수 있는 일수입니다. 0이면 하루만 늦어도 플랜이 늦어지는 중요한 작업(빨간색), 마이너스면 마감을 지킬 수 없습니다." },
  { key: "percent", label: "진척", width: 56, align: "right", editable: true, help: "얼마나 끝났는지 %로 넣습니다. 단계는 하위 작업에서 자동으로 계산되므로 입력하지 않습니다." },
  { key: "preds", label: "선행", width: 110, editable: true, help: "이 작업 앞에 끝나야 하는 작업의 행 번호입니다. 예) 3 → 3번이 끝나면 시작 · 3+2 → 끝나고 2일 뒤 · 5SS → 5번과 같이 시작 · 7FF → 7번과 같이 끝" },
  { key: "tasks", label: "태스크", width: 64, align: "right", help: "이 일정에 연결된 실행 작업의 완료/전체 수입니다. 단계면 하위 일정의 태스크까지 합칩니다." },
  { key: "gate", label: "검수", width: 108, align: "right", help: "연결된 작업의 검수입니다. 반려가 있으면 붉게 보이고, 누르면 그 작업을 엽니다." },
  { key: "remaining", label: "남은 기간", width: 96, align: "right", help: "진척 기준일부터 종료일까지 남은 근무일입니다. 100% 완료면 —, 종료일이 지났는데 끝나지 않았으면 늦은 만큼 음수입니다." },
  { key: "note", label: "메모", width: 160, editable: true, help: "이 작업이 무엇을 하는지, 또는 공유할 메모입니다. 인스펙터의 정의/메모와 같습니다." },
];
/** columns the user may hide in settings */
export const OPTIONAL_COLUMNS: Array<{ key: ColKey; label: string }> = COLS.filter((c) => c.key !== "row" && c.key !== "name").map((c) => ({ key: c.key, label: c.label }));

export function setHiddenColumns(hidden: string[]) {
  const state = useProjectStore.getState();
  state.setUI({
    hiddenColumns: hidden,
    gridWidth: nextGridWidthForHiddenColumns(state.ui.gridWidth, COLS, hidden),
  });
}

export const EDITABLE_ORDER: ColKey[] = ["name", "assignee", "mm", "duration", "start", "finish", "percent", "preds", "note"];

/** 하위가 있는 단계(요약)는 이름·선행·메모만 직접 입력한다. 나머지 셀은 에디터 진입 자체를 막는다. */
export function isCellEditable(col: ColKey, isSummary: boolean): boolean {
  const def = COLS.find((c) => c.key === col);
  if (!def?.editable) return false;
  if (isSummary) return col === "name" || col === "preds" || col === "note";
  return true;
}

const PIN_ORDER: Record<PinBoundary, ColKey[]> = {
  name: ["row", "wbs", "name"],
  wbs: ["row", "wbs"],
  none: [],
};

interface PinOffset {
  left: number;
  isLast: boolean;
}

function buildPinLayout(cols: Col[], nameWidth: number, boundary: PinBoundary): Map<ColKey, PinOffset> {
  const order = PIN_ORDER[boundary];
  const pinned = cols.filter((c) => order.includes(c.key));
  const layout = new Map<ColKey, PinOffset>();
  let left = 0;
  pinned.forEach((c, i) => {
    layout.set(c.key, { left, isLast: i === pinned.length - 1 });
    left += c.key === "name" ? nameWidth : c.width;
  });
  return layout;
}

function pinStyle(pin: PinOffset | undefined, zIndex: number | undefined, extra?: CSSProperties): CSSProperties {
  return {
    ...extra,
    ...(pin
      ? { position: "sticky", left: pin.left, zIndex: zIndex ?? 10 }
      : zIndex !== undefined
      ? { position: "relative", zIndex }
      : {}),
  };
}

function useVisibleCols(): { cols: Col[]; fixed: number } {
  const hidden = useProjectStore((s) => s.ui.hiddenColumns);
  return useMemo(() => {
    const cols = COLS.filter((c) => !hidden.includes(c.key));
    return { cols, fixed: cols.filter((c) => c.key !== "name").reduce((a, c) => a + c.width, 0) };
  }, [hidden]);
}

export interface CellFocus {
  rowId: string;
  col: ColKey;
}

export function WbsGrid({ viewport, paneWidth }: { viewport: RowsViewport; paneWidth?: number }) {
  const { rows, rowHeight, start, end } = viewport;
  const gridWidth = useProjectStore((s) => s.ui.gridWidth);
  const hiddenColumns = useProjectStore((s) => s.ui.hiddenColumns);
  const pinnedColumn = useProjectStore((s) => s.ui.pinnedColumn);
  const { cols, fixed } = useVisibleCols();
  const effectiveWidth = Math.max(gridWidth, paneWidth ?? 0);
  const nameWidth = visibleNameColumnWidth(effectiveWidth, fixed);
  const totalWidth = fixed + nameWidth;
  const pinLayout = useMemo(() => buildPinLayout(cols, nameWidth, pinnedColumn), [cols, nameWidth, pinnedColumn]);

  const [focus, setFocus] = useState<CellFocus | null>(null);
  const [editing, setEditing] = useState<CellFocus | null>(null);
  const surface = usePlanSurface();
  const self = useSelfRef();
  const selfResourceId = self.resourceId;
  const selfName = self.name;
  const canEditCell = useCallback(
    (col: ColKey, isSummary: boolean, rowId: string) => {
      const task = useProjectStore.getState().project.tasks.find((item) => item.id === rowId);
      const own = !!task && isOwnScheduleTask(task, { resourceId: selfResourceId, name: selfName });
      return isCellEditable(col, isSummary) && columnWritable(col, surface, own);
    },
    [surface, selfResourceId, selfName],
  );
  const focusId = useProjectStore((s) => s.focusId);
  const select = useProjectStore((s) => s.select);
  const toggleSelect = useProjectStore((s) => s.toggleSelect);
  const selection = useProjectStore((s) => s.selection);
  const anchor = useRef<string | null>(null);

  // follow store focus (e.g. from insights panel)
  useEffect(() => {
    if (focusId && focus?.rowId !== focusId) setFocus((f) => ({ rowId: focusId, col: f?.col ?? "name" }));
  }, [focusId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRowClick = useCallback(
    (e: React.MouseEvent, id: string, col: ColKey) => {
      if (e.shiftKey && anchor.current) {
        const a = rows.indexOf(anchor.current);
        const b = rows.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          select(rows.slice(lo, hi + 1), id);
        }
      } else if (e.metaKey || e.ctrlKey) {
        toggleSelect(id);
      } else {
        select([id], id);
        anchor.current = id;
      }
      setFocus({ rowId: id, col });
    },
    [rows, select, toggleSelect],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  /** 키가 큰 그리드에 focus()하면 overflow 조상이 스크롤되어 상단 툴바가 밀린다. */
  const focusGrid = useCallback(() => {
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  const onEditNext = useCallback(
    (rowId: string, col: ColKey, backwards?: boolean) => {
      const visibleKeys = new Set(cols.map((c) => c.key));
      const order = EDITABLE_ORDER.filter((k) => visibleKeys.has(k));
      const delta = backwards ? -1 : 1;

      let curRowIdx = rows.indexOf(rowId);
      let curColIdx = order.indexOf(col);
      if (curRowIdx === -1 || curColIdx === -1) {
        setEditing(null);
        focusGrid();
        return;
      }

      while (true) {
        curColIdx += delta;
        if (curColIdx < 0) {
          curRowIdx -= 1;
          if (curRowIdx < 0) {
            setEditing(null);
            focusGrid();
            return;
          }
          curColIdx = order.length - 1;
        } else if (curColIdx >= order.length) {
          curRowIdx += 1;
          if (curRowIdx >= rows.length) {
            setEditing(null);
            focusGrid();
            return;
          }
          curColIdx = 0;
        }

        const nextRowId = rows[curRowIdx];
        const nextCol = order[curColIdx];
        if (!nextRowId || !nextCol) {
          setEditing(null);
          focusGrid();
          return;
        }
        const sched = useProjectStore.getState().derived.schedule.tasks.get(nextRowId);
        if (canEditCell(nextCol, !!sched?.isSummary, nextRowId)) {
          select([nextRowId], nextRowId);
          setFocus({ rowId: nextRowId, col: nextCol });
          setEditing({ rowId: nextRowId, col: nextCol });
          return;
        }
      }
    },
    [cols, rows, select, focusGrid, canEditCell],
  );

  const onKeyDown = useGridKeyboard({ rows, cols, focus, setFocus, editing, setEditing, canEditCell, editWbs: surface.editWbs });

  const onNameColumnBestFit = useCallback(() => {
    const state = useProjectStore.getState();
    const fitRows = nameFitRowsFromProject(
      rows,
      state.project.tasks,
      state.derived.schedule,
      issueTaskIdSet(state.derived.health.issues),
    );
    const fitted = bestFitNameColumnWidth(fitRows, (text) =>
      measureNameText(text, nameColumnFontFromElement(containerRef.current)),
    );
    state.setUI({ gridWidth: bestFitGridWidth(fitted, fixed) });
  }, [fixed, rows]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="relative outline-none"
      style={{ width: totalWidth, height: Math.max(HEADER_H + rows.length * rowHeight + 120, viewport.viewportHeight) }}
      data-grid
    >
      <div
        className="sticky top-0 z-40 flex border-b border-border bg-background/95 text-body text-foreground/80 backdrop-blur"
        style={{ height: HEADER_H, width: totalWidth }}
      >
        {cols.map((c) => (
          <HeaderCell
            key={c.key}
            col={c}
            width={c.key === "name" ? nameWidth : c.width}
            pin={pinLayout.get(c.key)}
            onNameBestFit={c.key === "name" ? onNameColumnBestFit : undefined}
          />
        ))}
      </div>
      <div className="relative" style={{ height: rows.length * rowHeight }}>
        {rows.slice(start, end).map((id, i) => (
          <Row
            key={id}
            id={id}
            index={start + i}
            top={(start + i) * rowHeight}
            height={rowHeight}
            nameWidth={nameWidth}
            cols={cols}
            pinLayout={pinLayout}
            selected={selection.includes(id)}
            focusCol={focus?.rowId === id ? focus.col : null}
            editingCol={editing?.rowId === id ? editing.col : null}
            cellEditable={canEditCell}
            onClick={onRowClick}
            onEdit={(col) => {
              const sched = useProjectStore.getState().derived.schedule.tasks.get(id);
              if (canEditCell(col, !!sched?.isSummary, id)) setEditing({ rowId: id, col });
            }}
            onEditDone={() => {
              setEditing(null);
              focusGrid();
            }}
            onEditNext={onEditNext}
          />
        ))}
      </div>
    </div>
  );
}

function HeaderCell({
  col,
  width,
  pin,
  onNameBestFit,
}: {
  col: Col;
  width: number;
  pin?: PinOffset;
  onNameBestFit?: () => void;
}) {
  if (col.key === "row") {
    return (
      <div
        className={cn(
          "group/hdr flex items-end justify-center border-r border-grid-line px-0.5 pb-1.5 last:border-r-0",
          pin && "bg-background",
          pin?.isLast && "border-r-2 border-r-border",
        )}
        style={pinStyle(pin, 40, { width })}
      >
        <WbsColumnMenu />
      </div>
    );
  }
  const inner = (
    <div
        className={cn(
          "group/hdr relative flex items-end overflow-hidden border-r border-grid-line px-2 pb-1.5 last:border-r-0",
          col.align === "right" && "justify-end",
          pin && "bg-background",
          pin?.isLast && "border-r-2 border-r-border",
        )}
        style={pinStyle(pin, 40, { width })}
      >
      <span className="inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap">
        {col.label}
        {col.help && (
          <QuestionCircle12Regular
            className="size-3 shrink-0 text-muted-foreground/45 transition-colors group-hover/hdr:text-muted-foreground"
          />
        )}
      </span>
      {onNameBestFit && (
        <button
          type="button"
          aria-label="작업 열을 가장 긴 이름에 맞춥니다"
          title="더블클릭으로 이름에 맞추기"
          className="absolute inset-y-0 right-0 z-20 w-2 cursor-col-resize"
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNameBestFit();
          }}
        />
      )}
    </div>
  );
  if (!col.help) return inner;
  return (
    <Tooltip delayDuration={250}>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent side="bottom" align={col.align === "right" ? "end" : "start"} className="max-w-72 leading-[16px]">
        <span className="font-normal">{col.label}</span> · {col.help}
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------- rows ----------------

interface RowProps {
  id: string;
  index: number;
  top: number;
  height: number;
  nameWidth: number;
  cols: Col[];
  pinLayout: Map<ColKey, PinOffset>;
  selected: boolean;
  focusCol: ColKey | null;
  editingCol: ColKey | null;
  onClick: (e: React.MouseEvent, id: string, col: ColKey) => void;
  cellEditable: (col: ColKey, isSummary: boolean, rowId: string) => boolean;
  onEdit: (col: ColKey) => void;
  onEditDone: () => void;
  onEditNext?: (rowId: string, col: ColKey, backwards?: boolean) => void;
}

const Row = memo(function Row({ id, index, top, height, nameWidth, cols, pinLayout, selected, focusCol, editingCol, cellEditable, onClick, onEdit, onEditDone, onEditNext }: RowProps) {
  const task = useProjectStore((s) => s.project.tasks.find((t) => t.id === id));
  const sched = useProjectStore((s) => s.derived.schedule.tasks.get(id));
  const issues = useProjectStore((s) => s.derived.health.issues);
  const collapsed = useProjectStore((s) => s.collapsed.has(id));
  const toggleCollapse = useProjectStore((s) => s.toggleCollapse);
  const showCritical = useProjectStore((s) => s.ui.showCritical);
  const predsText = useProjectStore((s) => formatPredecessors(s.project, id));
  const taskCount = useProjectStore((s) => {
    const { done, total } = countLinkedExecution(id, s.project.executionTasks, s.derived.schedule);
    return total === 0 ? "" : `${done}/${total}`;
  });
  /** `통과/전체|결함|대기` — a string so the selector stays referentially stable */
  const gateKey = useProjectStore((s) => {
    const r = countLinkedExecution(id, s.project.executionTasks, s.derived.schedule);
    return r.gateTotal === 0 ? "" : `${r.gatePassed}/${r.gateTotal}|${r.gateFailed}|${r.gateReady}`;
  });
  const openGateTask = () => {
    const st = useProjectStore.getState();
    const linked = tasksLinkedTo(id, st.project.executionTasks, st.derived.schedule);
    const pick = (status: string) => linked.find((t) => t.gateTags?.some((g) => g.status === status));
    const target = pick("failed") ?? pick("ready") ?? linked.find((t) => t.gateTags?.some((g) => g.status !== "passed"));
    if (target) st.setExecutionFocus(target.id, { tab: "details" });
  };
  const remainingKey = useProjectStore((s) => {
    const t = s.derived.schedule.tasks.get(id);
    if (!t) return "";
    const r = remainingInfo(t, s.project.statusDate, s.derived.calendar);
    return `${r.kind}:${r.days}`;
  });
  const remaining = useMemo<RemainingInfo | null>(() => {
    if (!remainingKey) return null;
    const [kind, days] = remainingKey.split(":");
    return { kind: kind as RemainingKind, days: Number(days) };
  }, [remainingKey]);
  const flash = useFlash(sched?.finish);
  const sidePanel = useProjectStore((s) => s.ui.sidePanel);
  const focusId = useProjectStore((s) => s.focusId);
  const isInspectingThis = sidePanel === "inspector" && focusId === id;

  if (!task || !sched) return null;
  const rowIssues = issues.filter((i) => i.taskIds.includes(id));

  const pinBg = selected
    ? "bg-[color-mix(in_srgb,var(--tint)_12%,var(--card))]"
    : sched.isSummary
      ? "bg-muted"
      : "bg-card";

  const cellCls = (c: Col, isEditing: boolean, isFocused: boolean, extra?: string | false) => {
    const pin = pinLayout.get(c.key);
    const cellBg = isEditing ? "bg-card" : pin ? pinBg : undefined;
    return cn(
      "flex h-full items-center border-r border-grid-line text-body last:border-r-0 whitespace-nowrap overflow-hidden transition-colors",
      c.key === "name" ? "px-2" : isEditing ? "p-0" : "px-2",
      c.align === "right" && "justify-end tabular",
      pin ? "z-10" : "relative",
      isFocused && !isEditing && "ring-2 ring-inset ring-tint",
      isEditing && "ring-2 ring-inset ring-tint",
      cellBg,
      pin?.isLast && "border-r-2 border-r-border",
      extra,
    );
  };

  return (
    <div
      className={cn(
        "group/row absolute left-0 flex w-full border-b border-grid-line",
        selected ? "bg-tint/8" : sched.isSummary ? "bg-muted/40" : "bg-card",
        sched.critical && showCritical && "shadow-[inset_2px_0_0_var(--critical)]",
      )}
      style={{ top, height }}
      data-row-id={id}
    >
      {cols.map((c) => {
        const w = c.key === "name" ? nameWidth : c.width;
        const canEdit = cellEditable(c.key, sched.isSummary, id);
        const isEditing = canEdit && editingCol === c.key;
        const isFocused = focusCol === c.key;
        const pin = pinLayout.get(c.key);
        const zIndex = pin
          ? (isEditing || isFocused ? 30 : 10)
          : (isEditing || isFocused ? 20 : undefined);
        const common = {
          style: pinStyle(pin, zIndex, { width: w }),
          onMouseDown: (e: React.MouseEvent) => onClick(e, id, c.key),
          onDoubleClick: () => canEdit && onEdit(c.key),
        };
        switch (c.key) {
          case "row":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground")}>
                {index + 1}
              </div>
            );
          case "wbs":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground tabular")}>
                {sched.wbs}
              </div>
            );
          case "name":
            return (
              <div
                key={c.key}
                {...common}
                className={cellCls(c, isEditing, isFocused, "gap-1")}
                style={pinStyle(pin, zIndex, { width: w, paddingLeft: 8 + sched.depth * 16 })}
              >
                {sched.isSummary ? (
                  <button
                    type="button"
                    className="-ml-1 flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-foreground/8"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse(id);
                    }}
                    aria-label={collapsed ? "펼치기" : "접기"}
                  >
                    {collapsed ? <ChevronRight16Regular className="size-3.5" /> : <ChevronDown16Regular className="size-3.5" />}
                  </button>
                ) : sched.isMilestone ? (
                  <Diamond12Regular className="size-3 shrink-0 text-muted-foreground" />
                ) : (
                  <span className="size-3 shrink-0" />
                )}
                {isEditing ? (
                  <TextEditor
                    initial={task.name}
                    className="flex-1 px-1"
                    onCommit={(v) => {
                      if (v !== task.name) useProjectStore.getState().updateTask(id, { name: v }, `이름 변경: ${v || "(빈 이름)"}`);
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span className={cn("truncate", sched.isSummary && "text-foreground", !task.name && "text-muted-foreground")}>
                    {task.name || "이름 없음"}
                  </span>
                )}
                {!isEditing && (
                  <div className="ml-auto flex shrink-0 items-center gap-1">
                    {rowIssues.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              "size-1.5 shrink-0 rounded-full",
                              rowIssues.some((i) => i.severity === "error") ? "bg-critical" : "bg-muted-foreground/60",
                            )}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-64">
                          <ul className="space-y-0.5">
                            {rowIssues.map((i) => (
                              <li key={i.code}>{i.title}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          tabIndex={-1}
                          className={cn(
                            "flex size-5 items-center justify-center rounded transition-all hover:bg-foreground/10 hover:text-foreground",
                            isInspectingThis
                              ? "bg-tint/15 text-tint opacity-100"
                              : "text-muted-foreground/50 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
                          )}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            const s = useProjectStore.getState();
                            s.select([id], id);
                            if (s.ui.sidePanel === "inspector" && s.focusId === id) {
                              s.setUI({ sidePanel: null });
                            } else {
                              s.setUI({ sidePanel: "inspector" });
                            }
                          }}
                          aria-label={isInspectingThis ? "인스펙터 닫기" : "인스펙터 열기"}
                        >
                          <PanelRight16Regular className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-caption">
                        {isInspectingThis ? "인스펙터 닫기" : "인스펙터 열기"}{" "}
                        <span className="text-muted-foreground">({modKey}I)</span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            );
          case "assignee":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "truncate text-muted-foreground")}>
                {isEditing && !sched.isSummary ? (
                  <TextEditor
                    initial={task.assignee ?? ""}
                    onCommit={(v) => {
                      if (v !== (task.assignee ?? "")) {
                        useProjectStore.getState().updateTask(id, { assignee: v || undefined }, `${task.name}: 담당 변경 ${v}`);
                      }
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span className={cn(task.assignee ? "text-foreground" : "text-muted-foreground/60")}>
                    {task.assignee || "—"}
                  </span>
                )}
              </div>
            );
          case "mm": {
            const displayMM = task.plannedMM !== undefined
              ? `${task.plannedMM.toFixed(1)}`
              : sched.isSummary
              ? ""
              : `${(sched.duration / 21).toFixed(1)}`;
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground")}>
                {isEditing && !sched.isSummary ? (
                  <NumberEditor
                    initial={task.plannedMM ?? Math.round((sched.duration / 21) * 10) / 10}
                    min={0}
                    step={0.1}
                    decimals
                    onCommit={(v) => {
                      if (v !== task.plannedMM) {
                        useProjectStore.getState().updateTask(id, { plannedMM: v }, `${task.name}: M/M ${v}`);
                      }
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span>{displayMM ? `${displayMM}M` : ""}</span>
                )}
              </div>
            );
          }
          case "duration":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, sched.isSummary && "text-muted-foreground")}>
                {isEditing && !sched.isSummary ? (
                  <NumberEditor
                    initial={task.duration}
                    min={0}
                    onCommit={(v) => {
                      if (v !== task.duration) useProjectStore.getState().updateTask(id, { duration: v }, `${task.name}: 기간 ${task.duration}d → ${v}d`);
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <>{sched.duration}d</>
                )}
              </div>
            );
          case "start":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, sched.isSummary || !task.constraint || task.constraint.auto ? "text-muted-foreground" : "")}>
                {isEditing && !sched.isSummary ? (
                  <DateEditor
                    initial={sched.start}
                    max={sched.finish}
                    onCommit={(v) => {
                      if (v && v !== sched.start) {
                        useProjectStore.getState().updateTask(id, { constraint: { type: "SNET", date: v } }, `${task.name}: 시작 제약(SNET) ${formatDate(v, "numeric")}`);
                      }
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span
                    title={
                      task.constraint?.auto
                        ? "추가한 자리에 맞춘 시작일 · 앞 작업을 연결하면 자동으로 풀림"
                        : task.constraint
                          ? `${task.constraint.type} 제약 ${formatDate(task.constraint.date, "full")}`
                          : "계산된 시작일"
                    }
                  >
                    {formatDate(sched.start, "numeric")}
                    {task.constraint && !task.constraint.auto && <span className="ml-0.5 text-muted-foreground">{task.constraint.type === "MSO" ? "!" : "≥"}</span>}
                  </span>
                )}
              </div>
            );
          case "finish":
            return (
              <div key={c.key} {...common} className={cn(cellCls(c, isEditing, isFocused, "text-muted-foreground"), flash && "flash-tint", sched.deadlineMissed && "text-critical")}>
                {isEditing && !sched.isSummary ? (
                  <DateEditor
                    initial={sched.finish}
                    min={sched.start}
                    onCommit={(v) => {
                      if (v && v !== sched.finish) {
                        const cal = useProjectStore.getState().derived.calendar;
                        const dur = task.duration === 0 && v <= sched.start ? 0 : Math.max(1, cal.workingDaysBetween(sched.start, v));
                        if (dur !== task.duration) {
                          useProjectStore.getState().updateTask(id, { duration: dur }, `${task.name}: 기간 ${task.duration}d → ${dur}d`);
                        }
                      }
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  formatDate(sched.finish, "numeric")
                )}
              </div>
            );
          case "float":
            return (
              <div
                key={c.key}
                {...common}
                className={cellCls(c, isEditing, isFocused, sched.totalFloat < 0 ? "text-critical" : sched.critical ? "text-foreground" : "text-muted-foreground")}
              >
                {sched.isSummary ? "" : `${sched.totalFloat}d`}
              </div>
            );
          case "percent":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused)}>
                {isEditing && !sched.isSummary ? (
                  <NumberEditor
                    initial={task.percentComplete}
                    min={0}
                    max={100}
                    onCommit={(v) => {
                      if (v !== task.percentComplete) useProjectStore.getState().updateTask(id, { percentComplete: v }, `${task.name}: 진척 ${task.percentComplete}% → ${v}%`);
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span className={cn(sched.isSummary && "text-muted-foreground")}>{Math.round(sched.percentComplete)}%</span>
                )}
              </div>
            );
          case "preds":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground")}>
                {isEditing ? (
                  <TextEditor
                    initial={predsText}
                    onCommit={(v) => {
                      if (v !== predsText) useProjectStore.getState().setPredecessorsFromText(id, v);
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span className="truncate">{predsText}</span>
                )}
              </div>
            );
          case "note":
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground")}>
                {isEditing ? (
                  <TextEditor
                    initial={task.note ?? ""}
                    onCommit={(v) => {
                      if (v !== (task.note ?? "")) {
                        useProjectStore.getState().updateTask(id, { note: v || undefined }, `${task.name}: 메모`);
                      }
                      onEditDone();
                    }}
                    onCancel={onEditDone}
                    onTab={(shift) => onEditNext?.(id, c.key, shift)}
                  />
                ) : (
                  <span className={cn("truncate", task.note ? "text-foreground" : "text-muted-foreground/60")} title={task.note}>
                    {task.note || "—"}
                  </span>
                )}
              </div>
            );
          case "tasks": {
            const complete = Boolean(taskCount) && taskCount.split("/")[0] === taskCount.split("/")[1];
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, !taskCount ? "text-muted-foreground/60" : complete ? "text-muted-foreground" : undefined)}>
                {taskCount || "—"}
                {taskCount && gateKey ? <span title={GATE_GLYPH_TITLE[gateGlyph(gateKey)]}> {gateGlyph(gateKey)}</span> : null}
              </div>
            );
          }
          case "gate": {
            if (!gateKey) {
              return (
                <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused, "text-muted-foreground/60")}>
                  —
                </div>
              );
            }
            const [ratio = "", failed = "0", ready = "0"] = gateKey.split("|");
            const nFailed = Number(failed);
            const allPassed = ratio.split("/")[0] === ratio.split("/")[1];
            const tone = nFailed > 0 ? "bg-red-500/15 text-red-600 dark:text-red-400" : allPassed ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : Number(ready) > 0 ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : "bg-muted text-muted-foreground";
            return (
              <div key={c.key} {...common} className={cellCls(c, isEditing, isFocused)}>
                <button
                  type="button"
                  className={cn("rounded-full px-1.5 text-[11px] leading-[18px] tabular-nums", tone)}
                  title={nFailed > 0 ? `반려 ${nFailed}건. 누르면 작업을 엽니다` : `검수 통과 ${ratio}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openGateTask();
                  }}
                >
                  {nFailed > 0 ? `${nFailed} 반려` : `통과 ${ratio}`}
                </button>
              </div>
            );
          }
          case "remaining":
            return (
              <div
                key={c.key}
                {...common}
                className={cellCls(c, isEditing, isFocused, remaining ? REMAINING_TONE[remaining.kind] : undefined)}
              >
                {remaining && remaining.kind !== "done" ? formatRemaining(remaining) : "—"}
              </div>
            );
        }
      })}
    </div>
  );
});

// ---------------- editors ----------------

function TextEditor({
  initial,
  onCommit,
  onCancel,
  onTab,
  className,
}: {
  initial: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
  onTab?: (shift: boolean) => void;
  className?: string;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const finish = (fn: () => void) => {
    if (done.current) return;
    done.current = true;
    fn();
  };

  return (
    <input
      ref={ref}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          finish(() => onCommit(v));
        } else if (e.key === "Escape") {
          finish(onCancel);
        } else if (e.key === "Tab") {
          e.preventDefault();
          finish(() => {
            onCommit(v);
            onTab?.(e.shiftKey);
          });
        }
      }}
      onBlur={() => finish(() => onCommit(v))}
      className={cn(
        "h-full w-full min-w-0 bg-transparent px-2 text-body font-normal outline-none ring-0 border-0 shadow-none rounded-none selection:bg-tint selection:text-white",
        className,
      )}
    />
  );
}

function NumberEditor({
  initial,
  min,
  max,
  step,
  decimals,
  onCommit,
  onCancel,
  onTab,
  className,
}: {
  initial: number;
  min?: number;
  max?: number;
  step?: number;
  decimals?: boolean;
  onCommit: (v: number) => void;
  onCancel: () => void;
  onTab?: (shift: boolean) => void;
  className?: string;
}) {
  const [v, setV] = useState(String(initial));
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const finish = (fn: () => void) => {
    if (done.current) return;
    done.current = true;
    fn();
  };

  const commit = (tabShift?: boolean) => {
    let n = Number(v.replace(/[^\d.-]/g, ""));
    if (!Number.isFinite(n)) {
      finish(() => {
        onCancel();
        if (tabShift !== undefined) onTab?.(tabShift);
      });
      return;
    }
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    const result = decimals || step ? Math.round(n * 10) / 10 : Math.round(n);
    finish(() => {
      onCommit(result);
      if (tabShift !== undefined) onTab?.(tabShift);
    });
  };

  return (
    <input
      ref={ref}
      value={v}
      step={step}
      inputMode="decimal"
      onChange={(e) => setV(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          commit();
        } else if (e.key === "Escape") {
          finish(onCancel);
        } else if (e.key === "Tab") {
          e.preventDefault();
          commit(e.shiftKey);
        }
      }}
      onBlur={() => commit()}
      className={cn(
        "h-full w-full min-w-0 bg-transparent px-2 text-right text-body font-normal tabular outline-none ring-0 border-0 shadow-none rounded-none selection:bg-tint selection:text-white",
        className,
      )}
    />
  );
}

/** Typed date (YYYY-MM-DD / MM-DD / M/D) with a shadcn Calendar anchored to the cell. */
function DateEditor({
  initial,
  onCommit,
  onCancel,
  onTab,
  min,
  max,
}: {
  initial: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
  onTab?: (shift: boolean) => void;
  min?: string;
  max?: string;
}) {
  // Seed with the same text the cell shows; parseTypedDate reads it back.
  const [v, setV] = useState(() => formatDate(initial, "numeric", { empty: "" }));
  const [open, setOpen] = useState(true);
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const finish = (fn: () => void) => {
    if (done.current) return;
    done.current = true;
    fn();
  };

  const commitTyped = (tabShift?: boolean) => {
    const iso = parseTypedDate(v, initial);
    finish(() => {
      if (iso) onCommit(clampEditedDate(iso, min, max));
      else onCancel();
      if (tabShift !== undefined) onTab?.(tabShift);
    });
  };

  const parsed = parseTypedDate(v, initial);
  const calendarValue = parsed && isISODate(parsed) ? parsed : isISODate(initial) ? initial : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) commitTyped();
      }}
    >
      <PopoverAnchor asChild>
        <div className="relative flex h-full w-full items-center">
          <input
            ref={ref}
            value={v}
            onChange={(e) => setV(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                commitTyped();
              } else if (e.key === "Escape") {
                finish(onCancel);
              } else if (e.key === "Tab") {
                e.preventDefault();
                commitTyped(e.shiftKey);
              }
            }}
            className="h-full w-full min-w-0 bg-transparent px-2 text-right text-body font-normal tabular outline-none ring-0 border-0 shadow-none rounded-none selection:bg-tint selection:text-white"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={1}
        className="w-auto p-0 shadow-lg border border-border bg-popover rounded-md overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DateCalendar
          value={calendarValue}
          defaultMonth={calendarValue}
          min={min}
          max={max}
          onSelect={(iso) => finish(() => onCommit(clampEditedDate(iso, min, max)))}
        />
      </PopoverContent>
    </Popover>
  );
}

function clampEditedDate(iso: string, min?: string, max?: string): string {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

function parseTypedDate(text: string, ref: string): string | null {
  const t = text
    .trim()
    .replace(/\s+/g, "")
    .replace(/년|월/g, "-")
    .replace(/일/g, "")
    .replace(/\./g, "-")
    .replace(/\//g, "-")
    .replace(/-+$/, "");
  if (isISODate(t)) return t;
  const refYear = Number(ref.slice(0, 4)) || new Date().getFullYear();
  // YYYY-MM-DD or YYYY-M-D
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return toISO(Number(m[1]), Number(m[2]), Number(m[3]));
  // YY-MM-DD or YY-M-D
  m = /^(\d{2})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) {
    const yr = Number(m[1]) < 70 ? 2000 + Number(m[1]) : 1900 + Number(m[1]);
    return toISO(yr, Number(m[2]), Number(m[3]));
  }
  // MM-DD or M-D
  m = /^(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return toISO(refYear, Number(m[1]), Number(m[2]));
  // YYYYMMDD (8 digits)
  m = /^(\d{4})(\d{2})(\d{2})$/.exec(t);
  if (m) return toISO(Number(m[1]), Number(m[2]), Number(m[3]));
  // MMDD (4 digits)
  m = /^(\d{2})(\d{2})$/.exec(t);
  if (m) return toISO(refYear, Number(m[1]), Number(m[2]));
  // D or DD (just day of the reference month)
  m = /^(\d{1,2})$/.exec(t);
  if (m) {
    const refMonth = Number(ref.slice(5, 7)) || 1;
    return toISO(refYear, refMonth, Number(m[1]));
  }
  return null;
}
function toISO(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
}

/** Returns true for ~1.2s after `value` changes (skips the first render). */
function useFlash(value: unknown): boolean {
  const [on, setOn] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      setOn(true);
      const t = setTimeout(() => setOn(false), 1200);
      return () => clearTimeout(t);
    }
  }, [value]);
  return on;
}

// ---------------- keyboard ----------------

function useGridKeyboard({
  rows,
  cols,
  focus,
  setFocus,
  editing,
  setEditing,
  canEditCell,
  editWbs,
}: {
  rows: string[];
  cols: Col[];
  focus: CellFocus | null;
  setFocus: (f: CellFocus | null) => void;
  editing: CellFocus | null;
  setEditing: (f: CellFocus | null) => void;
  canEditCell: (col: ColKey, isSummary: boolean, rowId: string) => boolean;
  editWbs: boolean;
}) {
  return useCallback(
    (e: React.KeyboardEvent) => {
      if (editing) return;
      const s = useProjectStore.getState();
      const mod = e.metaKey || e.ctrlKey;
      const idx = focus ? rows.indexOf(focus.rowId) : -1;
      const move = (delta: number) => {
        const next = rows[Math.max(0, Math.min(rows.length - 1, (idx < 0 ? 0 : idx) + delta))];
        if (!next) return;
        const col = focus?.col ?? "name";
        setFocus({ rowId: next, col });
        if (e.shiftKey && focus) {
          const a = rows.indexOf(focus.rowId);
          const b = rows.indexOf(next);
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          s.select(rows.slice(lo, hi + 1), next);
        } else s.select([next], next);
      };
      const id = focus?.rowId;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (e.altKey && id) {
            if (!editWbs) return hintBlockedEdit();
            s.moveDown(id);
          } else move(1);
          return;
        case "ArrowUp":
          e.preventDefault();
          if (e.altKey && id) {
            if (!editWbs) return hintBlockedEdit();
            s.moveUp(id);
          } else move(-1);
          return;
        case "ArrowLeft":
        case "ArrowRight": {
          if (!focus) return;
          e.preventDefault();
          const sched = s.derived.schedule.tasks.get(focus.rowId);
          if (focus.col === "name" && sched?.isSummary && !e.shiftKey) {
            const collapsed = s.collapsed.has(focus.rowId);
            if (e.key === "ArrowLeft" && !collapsed) return s.toggleCollapse(focus.rowId);
            if (e.key === "ArrowRight" && collapsed) return s.toggleCollapse(focus.rowId);
          }
          const visibleKeys = new Set(cols.map((c) => c.key));
          const order = EDITABLE_ORDER.filter((k) => visibleKeys.has(k));
          const i = order.indexOf(focus.col);
          const ni = Math.max(0, Math.min(order.length - 1, (i < 0 ? 0 : i) + (e.key === "ArrowRight" ? 1 : -1)));
          setFocus({ rowId: focus.rowId, col: order[ni] ?? "name" });
          return;
        }
        case "Enter":
          e.preventDefault();
          if (mod && e.shiftKey && id) {
            if (!editWbs) return hintBlockedEdit();
            return void s.addChild(id);
          }
          if (mod && id) {
            if (!editWbs) return hintBlockedEdit();
            return void s.addSibling(id);
          }
          if (focus) {
            const target = s.derived.schedule.tasks.get(focus.rowId);
            const summary = !!target?.isSummary;
            if (canEditCell(focus.col, summary, focus.rowId)) setEditing(focus);
            else if (isCellEditable(focus.col, summary)) hintBlockedEdit();
          }
          return;
        case "Tab":
          e.preventDefault();
          if (!s.selection.length) return;
          if (!editWbs) return hintBlockedEdit();
          if (e.shiftKey) s.outdent(s.selection);
          else s.indent(s.selection);
          return;
        case "Backspace":
        case "Delete":
          if (mod || e.key === "Delete") {
            e.preventDefault();
            if (!editWbs) return hintBlockedEdit();
            s.requestDeleteTasks(s.selection);
          }
          return;
        case " ":
          if (id) {
            e.preventDefault();
            const sched = s.derived.schedule.tasks.get(id);
            if (sched?.isSummary) s.toggleCollapse(id);
            else {
              s.setUI({ sidePanel: s.ui.sidePanel === "inspector" ? null : "inspector" });
            }
          }
          return;
        case "Escape":
          s.select([], null);
          setFocus(null);
          return;
        case "a":
          if (mod) {
            e.preventDefault();
            s.select(rows, focus?.rowId ?? null);
          }
          return;
        default:
          // start editing on printable key
          if (focus && !mod && !e.altKey && e.key.length === 1) {
            const target = s.derived.schedule.tasks.get(focus.rowId);
            const summary = !!target?.isSummary;
            if (canEditCell(focus.col, summary, focus.rowId)) setEditing(focus);
            else if (isCellEditable(focus.col, summary)) hintBlockedEdit();
          }
      }
    },
    [rows, cols, focus, editing, setFocus, setEditing, canEditCell, editWbs],
  );
}

export function WbsColumnMenu() {
  const hidden = useProjectStore((s) => s.ui.hiddenColumns);
  const pinnedColumn = useProjectStore((s) => s.ui.pinnedColumn);
  const setUI = useProjectStore((s) => s.setUI);
  const visibleCount = OPTIONAL_COLUMNS.filter((c) => !hidden.includes(c.key)).length + 1;

  const toggleCol = (key: ColKey, show: boolean) => {
    setHiddenColumns(show ? hidden.filter((k) => k !== key) : [...hidden, key]);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-muted-foreground transition-colors hover:bg-card hover:text-foreground hover:shadow-xs"
          aria-label="열 및 좌측 고정 설정"
        >
          <TableSimple16Regular />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>좌측 틀 고정</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={pinnedColumn} onValueChange={(v) => setUI({ pinnedColumn: v as PinBoundary })}>
          <DropdownMenuRadioItem value="name">작업까지 고정</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="wbs">WBS까지 고정</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="none">고정 안 함</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>
          표시할 열 ({visibleCount - 1}/{OPTIONAL_COLUMNS.length})
        </DropdownMenuLabel>
        {OPTIONAL_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.key}
            checked={!hidden.includes(col.key)}
            onCheckedChange={(v) => toggleCol(col.key, v === true)}
            onSelect={(e) => e.preventDefault()}
          >
            {col.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setHiddenColumns([...DEFAULT_HIDDEN_COLUMNS])}>
          기본 열로 복원
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setHiddenColumns([])}>
          모든 열 표시
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type { Task, HealthIssue };
